/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html } from 'lit';
import { query } from 'lit/decorators/query.js';
import { animate, AnimationModalEnterName, i18n, I18nService, isScrollable, property, reverseAnimation, querySlot, getElementUpdates, } from '@cds/core/internal';
import { CdsInternalOverlay } from '@cds/core/internal-components/overlay';
import styles from './modal.element.scss';
/**
 * Web component modal.
 *
 * ```typescript
 * import '@cds/core/modal/register.js';
 * ```
 *
 * ```html
 * <cds-modal>
 *   <cds-modal-header>
 *      <h3 cds-text="title">My Modal</h3>
 *   </cds-modal-header>
 *   <cds-modal-content>
 *     <p>Lorem Ipsum</p>
 *   </cds-modal-content>
 *   <cds-modal-actions>
 *       <cds-button>Ok</cds-button>
 *   </cds-modal-actions>
 * </cds-modal>
 * ```
 *
 * @element cds-modal
 * @slot
 * @slot cds-modal-content
 * @slot cds-modal-header
 * @slot cds-modal-actions
 * @event closeChange - notify when the user has clicked the close button
 * @cssprop --backdrop-background - inherited from the internal overlay component
 * @cssprop --layered-backdrop-background - inherited from the internal overlay component
 * @cssprop --border-color
 * @cssprop --border-width
 * @cssprop --border-radius
 * @cssprop --background
 * @cssprop --box-shadow
 * @cssprop --width
 * @cssprop --content-overflow - set as { x, y }. take care when customizing because overflow settings can have unintended side effects.
 * @cssprop --max-height - sets hard limit on height of modal
 * @cssprop --tablet-max-height - sets hard limit on height of modal when on a tablet in landscape mode
 */
let CdsModal = class CdsModal extends CdsInternalOverlay {
    constructor() {
        super(...arguments);
        this.i18n = I18nService.keys.modal;
        /** If false, the modal will not render the close button.  */
        this.closable = true;
        this.isScrollable = false;
        this.observers = [];
    }
    get customBumpers() {
        return [this.modalHeader, this.modalFooter];
    }
    static get styles() {
        return [...super.styles, styles];
    }
    // modal-body requires a tab index so it can be scrolled
    render() {
        return html `
      ${this.backdropTemplate}
      <div class="modal-dialog private-host" tabindex="-1" cds-layout="m:md m@md:xl">
        <div cds-layout="display:screen-reader-only">${this.i18n.contentStart}</div>
        <div class="modal-content" cds-layout="vertical gap:sm gap@md:lg align:stretch p-y:lg">
          <div cds-layout="horizontal gap:sm wrap:none align:vertical-center p-x:lg">
            <div>
              <slot name="modal-header"></slot>
            </div>
            <div cds-layout="align:right">
              <slot name="modal-header-actions"></slot>
            </div>
            ${this.closable ? this.closeButtonTemplate : ''}
          </div>
          <div class="modal-body" cds-layout="p-x:lg">
            <slot></slot>
          </div>
          <div cds-layout="align-stretch p-x:lg" ?hidden=${!this.modalFooter}>
            <slot name="modal-actions" @slotchange=${() => this.requestUpdate()}></slot>
          </div>
        </div>
        <div cds-layout="display:screen-reader-only">${this.i18n.contentEnd}</div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.observers.push(getElementUpdates(this, 'hidden', () => this.setScrollableProperties()));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.observers.forEach(o => o?.disconnect());
    }
    async setScrollableProperties() {
        const contentElement = this.content;
        if (this.hidden === false && contentElement !== null) {
            await this.updateComplete; // wait until after render to measure if scrollable
            this.isScrollable = isScrollable(contentElement);
            contentElement.tabIndex = this.isScrollable ? 0 : -1;
            contentElement.ariaLabel = this.isScrollable ? this.i18n.contentBox : null;
        }
    }
};
__decorate([
    i18n()
], CdsModal.prototype, "i18n", void 0);
__decorate([
    property({ type: Boolean })
], CdsModal.prototype, "closable", void 0);
__decorate([
    property({ type: String })
], CdsModal.prototype, "size", void 0);
__decorate([
    query('.modal-body')
], CdsModal.prototype, "content", void 0);
__decorate([
    querySlot('cds-modal-header')
], CdsModal.prototype, "modalHeader", void 0);
__decorate([
    querySlot('cds-modal-actions')
], CdsModal.prototype, "modalFooter", void 0);
CdsModal = __decorate([
    animate({
        hidden: {
            true: reverseAnimation(AnimationModalEnterName),
            false: AnimationModalEnterName,
        },
    })
], CdsModal);
export { CdsModal };
